import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAvailability } from '@hooks/usePublicBooking';
import { PageLoading } from '@components/common/LoadingSpinner';
import { ErrorMessage } from '@components/common/ErrorMessage';
import { Button } from '@components/ui/button';
import type { TimeSlot } from '@api/types';
import { cn, formatTime } from '@lib/utils';
import { nextDaysInTimezone } from '@lib/timezone';

interface DateTimeStepProps {
  centerSlug: string;
  serviceId: string;
  staffId?: string;
  /**
   * IANA timezone of the centre. Slot times and weekday labels render in this
   * zone so customers connecting from elsewhere see venue wall-clock time.
   */
  centerTimezone: string;
  onSelectSlot: (slot: TimeSlot) => void;
  onSelectDate: (date: string) => void;
  selectedDate: string | null;
  onBack: () => void;
}

export function DateTimeStep({
  centerSlug,
  serviceId,
  staffId,
  centerTimezone,
  onSelectSlot,
  onSelectDate,
  selectedDate,
  onBack,
}: DateTimeStepProps) {
  const { t } = useTranslation();
  // M-8 closeout: antes `getNextDays(14)` usaba `new Date()` +
  // `toISOString()` (UTC del navegador). Para un cliente en UTC-5
  // reservando en un centro de Madrid, la franja se desalineaba 1 día
  // en horas de la noche. El helper trabaja en TZ del centro.
  const days = nextDaysInTimezone(14, centerTimezone);
  const [activeDate, setActiveDate] = useState<string>(selectedDate ?? days.at(0) ?? '');

  const params: Record<string, string> = {
    serviceId,
    date: activeDate,
  };
  if (staffId) params.staffId = staffId;

  const { data: slots, isLoading, isError, refetch } = useAvailability(centerSlug, params);

  const handleDateChange = (date: string) => {
    setActiveDate(date);
    onSelectDate(date);
  };

  const availableSlots = slots?.filter((s) => s.available) ?? [];

  return (
    <section aria-label={t('widget.selectDate')}>
      <h2 className="mb-4 text-lg font-semibold">{t('widget.selectDate')}</h2>

      {/* Date picker strip */}
      <div
        className="mb-6 flex gap-2 overflow-x-auto pb-2"
        role="group"
        aria-label="Selecciona una fecha"
      >
        {days.map((day) => {
          const d = new Date(day + 'T00:00:00');
          return (
            <button
              key={day}
              type="button"
              onClick={() => { handleDateChange(day); }}
              className={cn(
                'flex shrink-0 flex-col items-center rounded-md border px-3 py-2 text-xs transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                activeDate === day
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'hover:border-primary hover:bg-primary/5',
              )}
              aria-pressed={activeDate === day}
              data-testid={`date-option-${day}`}
            >
              <span className="font-medium">{d.getDate()}</span>
              <span className="text-[10px] uppercase opacity-80">
                {d.toLocaleDateString('es-ES', { weekday: 'short', timeZone: centerTimezone })}
              </span>
            </button>
          );
        })}
      </div>

      {/* Time slots */}
      <h2 className="mb-3 text-base font-medium">{t('widget.selectTime')}</h2>

      {isLoading && <PageLoading />}
      {isError && <ErrorMessage onRetry={() => void refetch()} />}

      {!isLoading && !isError && availableSlots.length === 0 && (
        <p className="text-sm text-muted-foreground" role="status">
          {t('widget.noAvailability')}
        </p>
      )}

      {!isLoading && availableSlots.length > 0 && (
        <ul className="grid grid-cols-3 gap-2" role="list" data-testid="time-slots">
          {availableSlots.map((slot) => (
            <li key={slot.startTime}>
              <button
                type="button"
                onClick={() => { onSelectSlot(slot); }}
                className={cn(
                  'w-full rounded-md border bg-card py-2 text-sm font-medium transition-colors hover:border-primary hover:bg-primary/5',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                )}
                data-testid={`slot-option-${slot.startTime}`}
              >
                {formatTime(slot.startTime, { timezone: centerTimezone })}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-6">
        <Button type="button" variant="outline" onClick={onBack} data-testid="datetime-back">
          {t('common.back')}
        </Button>
      </div>
    </section>
  );
}
